import { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { Users, Search, ChevronRight, Loader2, Phone, Mail, Building2, Plus, X, Pencil } from "lucide-react";
import { toast } from "sonner";
import api, { formatError } from "@/services/api";

const PAGE_SIZE = 25;
const inp = "w-full px-3 py-2 bg-white border border-[#E5E5E5] rounded-lg text-sm text-[#181818] focus:outline-none focus:border-[#0176D3]/50 focus:ring-1 focus:ring-[#0176D3]/20";

const CHANNEL_ICONS = {
  EMAIL:     Mail,
  WHATSAPP:  Phone,
  PHONE:     Phone,
};

function CreateContactModal({ onClose, onCreated }) {
  const [form, setForm] = useState({ first_name: "", last_name: "", email: "", phone: "", title: "", account_id: "", notes: "" });
  const [errors, setErrors] = useState({});
  const [saving, setSaving] = useState(false);
  const [accounts, setAccounts] = useState([]);

  useEffect(() => {
    api.get("/crm/accounts", { params: { limit: 50 }}).then(r => setAccounts(r.data.results || [])).catch(() => {});
  }, []);

  const set = (k, v) => { setForm(p => ({...p, [k]: v})); setErrors(p => ({...p, [k]: "", name: "", contact: ""})); };

  const validate = () => {
    const e = {};
    if (!form.first_name.trim() && !form.last_name.trim()) e.name = "First or last name is required";
    if (!form.email.trim() && !form.phone.trim()) e.contact = "At least one contact method (email or phone) is required";
    setErrors(e);
    return Object.keys(e).length === 0;
  };

  const submit = async () => {
    if (!validate()) return;
    setSaving(true);
    try {
      const { data } = await api.post("/crm/contacts", form);
      toast.success("Contact created");
      onCreated(data);
    } catch (e) {
      const status = e?.response?.status;
      const detail = e?.response?.data?.detail;
      if (status === 409 && detail?.field) {
        // Duplicate contact detected — show inline on the conflicting field
        setErrors({ [detail.field]: `A contact with this ${detail.field} already exists: "${detail.existing_name || detail.existing_id}"` });
        toast.warning(`Duplicate ${detail.field} detected.`);
      } else if (Array.isArray(detail)) {
        const errs = {};
        detail.forEach(d => { errs[d.field] = d.message; });
        setErrors(errs);
      } else {
        toast.error(typeof detail === "string" ? detail : "Failed to create contact");
      }
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/30 p-4">
      <div className="bg-white rounded-xl border border-[#E5E5E5] w-full max-w-lg shadow-xl">
        <div className="flex items-center justify-between px-5 pt-5 pb-4 border-b border-[#E5E5E5]">
          <div className="flex items-center gap-2"><Users className="w-4 h-4 text-[#0176D3]" /><h2 className="text-base font-semibold text-[#181818]">New Contact</h2></div>
          <button onClick={onClose}><X className="w-4 h-4 text-[#706E6B]" /></button>
        </div>
        <div className="px-5 py-4 space-y-4">
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-xs font-medium text-[#444] mb-1">First Name <span className="text-rose-500">*</span></label>
              <input value={form.first_name} onChange={e => set("first_name", e.target.value)} placeholder="Ananya" data-testid="contact-first-name" className={`${inp}${errors.name ? " border-rose-400" : ""}`} />
            </div>
            <div>
              <label className="block text-xs font-medium text-[#444] mb-1">Last Name</label>
              <input value={form.last_name} onChange={e => set("last_name", e.target.value)} placeholder="Sharma" data-testid="contact-last-name" className={inp} />
            </div>
          </div>
          {errors.name && <p data-testid="err-contact-name" className="text-xs text-rose-500 -mt-2">{errors.name}</p>}
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-xs font-medium text-[#444] mb-1">Email <span className="text-rose-500">*</span></label>
              <input type="email" value={form.email} onChange={e => set("email", e.target.value)} placeholder="ananya@example.com" data-testid="contact-email" className={`${inp}${errors.contact ? " border-rose-400" : ""}`} />
            </div>
            <div>
              <label className="block text-xs font-medium text-[#444] mb-1">Phone</label>
              <input value={form.phone} onChange={e => set("phone", e.target.value)} placeholder="+91 …" data-testid="contact-phone" className={inp} />
            </div>
          </div>
          {errors.contact && <p data-testid="err-contact-contact" className="text-xs text-rose-500 -mt-2">{errors.contact}</p>}
          <div>
            <label className="block text-xs font-medium text-[#444] mb-1">Title / Role</label>
            <input value={form.title} onChange={e => set("title", e.target.value)} placeholder="Event Coordinator" className={inp} />
          </div>
          <div>
            <label className="block text-xs font-medium text-[#444] mb-1">Linked Account</label>
            <select value={form.account_id} onChange={e => set("account_id", e.target.value)} className={inp}>
              <option value="">— No account —</option>
              {accounts.map(a => <option key={a.id} value={a.id}>{a.name}</option>)}
            </select>
          </div>
          <div>
            <label className="block text-xs font-medium text-[#444] mb-1">Notes</label>
            <textarea rows={2} value={form.notes} onChange={e => set("notes", e.target.value)} className={`${inp} resize-none`} />
          </div>
        </div>
        <div className="flex justify-end gap-2 px-5 pb-5">
          <button onClick={onClose} className="px-4 py-2 text-sm text-[#706E6B]">Cancel</button>
          <button data-testid="contact-submit-btn" onClick={submit} disabled={saving} className="px-4 py-2 text-sm bg-[#0176D3] text-white rounded-lg hover:bg-[#0060B0] disabled:opacity-50">
            {saving ? "Saving…" : "Create Contact"}
          </button>
        </div>
      </div>
    </div>
  );
}

function EditContactModal({ contact, onClose, onUpdated }) {
  const original = {
    first_name: contact.first_name || "",
    last_name:  contact.last_name  || "",
    email:      contact.email  || "",
    phone:      contact.phone  || "",
    title:      contact.title  || "",
    notes:      contact.notes  || "",
    account_id: contact.customer_id || contact.account_id || "",
  };
  const [form, setForm]     = useState({ ...original });
  const [errors, setErrors] = useState({});
  const [saving, setSaving] = useState(false);
  const [accounts, setAccounts] = useState([]);

  useEffect(() => {
    api.get("/crm/accounts", { params: { limit: 50 } }).then(r => setAccounts(r.data.results || [])).catch(() => {});
  }, []);

  const set = (k, v) => { setForm(p => ({ ...p, [k]: v })); setErrors(p => ({ ...p, [k]: "" })); };

  const submit = async () => {
    const patch = {};
    if (form.first_name !== original.first_name) patch.first_name = form.first_name;
    if (form.last_name  !== original.last_name)  patch.last_name  = form.last_name;
    if (form.email      !== original.email)       patch.email      = form.email;
    if (form.phone      !== original.phone)       patch.phone      = form.phone;
    if (form.title      !== original.title)       patch.title      = form.title;
    if (form.notes      !== original.notes)       patch.notes      = form.notes;
    if (form.account_id !== original.account_id)  patch.customer_id = form.account_id;

    if (Object.keys(patch).length === 0) { toast.info("No changes to save"); onClose(); return; }
    if (!form.first_name.trim() && !form.last_name.trim()) {
      setErrors({ name: "First or last name is required" }); return;
    }

    setSaving(true);
    try {
      await api.patch(`/crm/contacts/${contact.id}`, patch);
      toast.success("Contact updated");
      onUpdated();
      onClose();
    } catch (e) {
      const detail = e?.response?.data?.detail;
      if (typeof detail === "string") toast.error(detail);
      else toast.error(formatError(e));
    } finally { setSaving(false); }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/30 p-4">
      <div className="bg-white rounded-xl border border-[#E5E5E5] w-full max-w-lg shadow-xl">
        <div className="flex items-center justify-between px-5 pt-5 pb-4 border-b border-[#E5E5E5]">
          <div className="flex items-center gap-2">
            <Pencil className="w-4 h-4 text-[#0176D3]" />
            <h2 className="text-base font-semibold text-[#181818]">Edit Contact</h2>
          </div>
          <button onClick={onClose}><X className="w-4 h-4 text-[#706E6B]" /></button>
        </div>
        <div className="px-5 py-4 space-y-4">
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-xs font-medium text-[#444] mb-1">First Name</label>
              <input value={form.first_name} onChange={e => set("first_name", e.target.value)} data-testid="edit-contact-first-name" className={`${inp}${errors.name ? " border-rose-400" : ""}`} />
            </div>
            <div>
              <label className="block text-xs font-medium text-[#444] mb-1">Last Name</label>
              <input value={form.last_name} onChange={e => set("last_name", e.target.value)} data-testid="edit-contact-last-name" className={inp} />
            </div>
          </div>
          {errors.name && <p className="text-xs text-rose-500 -mt-2">{errors.name}</p>}
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-xs font-medium text-[#444] mb-1">Email</label>
              <input type="email" value={form.email} onChange={e => set("email", e.target.value)} data-testid="edit-contact-email" className={inp} />
            </div>
            <div>
              <label className="block text-xs font-medium text-[#444] mb-1">Phone</label>
              <input value={form.phone} onChange={e => set("phone", e.target.value)} data-testid="edit-contact-phone" className={inp} />
            </div>
          </div>
          <div>
            <label className="block text-xs font-medium text-[#444] mb-1">Title / Role</label>
            <input value={form.title} onChange={e => set("title", e.target.value)} className={inp} />
          </div>
          <div>
            <label className="block text-xs font-medium text-[#444] mb-1">Linked Account</label>
            <select value={form.account_id} onChange={e => set("account_id", e.target.value)} className={inp}>
              <option value="">— No account —</option>
              {accounts.map(a => <option key={a.id} value={a.id}>{a.name}</option>)}
            </select>
          </div>
          <div>
            <label className="block text-xs font-medium text-[#444] mb-1">Notes</label>
            <textarea rows={2} value={form.notes} onChange={e => set("notes", e.target.value)} className={`${inp} resize-none`} />
          </div>
        </div>
        <div className="flex justify-end gap-2 px-5 pb-5">
          <button onClick={onClose} className="px-4 py-2 text-sm text-[#706E6B]">Cancel</button>
          <button data-testid="edit-contact-submit-btn" onClick={submit} disabled={saving} className="px-4 py-2 text-sm bg-[#0176D3] text-white rounded-lg hover:bg-[#0060B0] disabled:opacity-50">
            {saving ? "Saving…" : "Save Changes"}
          </button>
        </div>
      </div>
    </div>
  );
}

export default function CRMContacts() {  const navigate = useNavigate();
  const [contacts, setContacts] = useState([]);
  const [total,    setTotal]    = useState(0);
  const [loading,  setLoading]  = useState(true);
  const [search,   setSearch]   = useState("");
  const [page,     setPage]     = useState(0);
  const [showCreate,   setShowCreate]   = useState(false);
  const [editContact,  setEditContact]  = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/crm/contacts", {
        params: {
          limit: PAGE_SIZE,
          skip: page * PAGE_SIZE,
          ...(search && { search }),
        },
      });
      setContacts(data.results || []);
      setTotal(data.total || 0);
    } catch (e) {
      toast.error(formatError(e));
    } finally {
      setLoading(false);
    }
  }, [page, search]);

  useEffect(() => {
    const t = setTimeout(() => load(), search ? 300 : 0);
    return () => clearTimeout(t);
  }, [load, search]);

  return (
    <div className="p-3 sm:p-6 max-w-7xl mx-auto space-y-5">
      {showCreate && <CreateContactModal onClose={() => setShowCreate(false)} onCreated={() => { setShowCreate(false); load(); }} />}
      {editContact && (
        <EditContactModal
          contact={editContact}
          onClose={() => setEditContact(null)}
          onUpdated={() => { setEditContact(null); load(); }}
        />
      )}
      {/* Header */}
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="flex items-center gap-3">
          <Users className="w-5 h-5 text-[#0176D3]" />
          <h1 className="text-xl font-bold text-[#181818] font-sans">Contacts</h1>
          <span className="text-xs font-mono text-[#706E6B] bg-[#F3F3F3] border border-[#E5E5E5] px-2 py-0.5 rounded">
            {total} total
          </span>
        </div>
        <button data-testid="new-contact-btn" onClick={() => setShowCreate(true)} className="flex items-center gap-1.5 px-3 py-1.5 bg-[#EEF4FF] border border-[#0176D3]/30 rounded-lg text-xs text-[#0176D3] hover:bg-[#EEF4FF] transition-colors font-medium">
          <Plus className="w-3.5 h-3.5" /> New Contact
        </button>
      </div>

      {/* Search */}
      <div className="bg-[#F3F3F3] border border-[#E5E5E5] rounded-xl p-4">
        <div className="relative max-w-sm">
          <Search className="w-3.5 h-3.5 absolute left-3 top-1/2 -translate-y-1/2 text-[#706E6B]" />
          <input
            value={search}
            onChange={e => { setSearch(e.target.value); setPage(0); }}
            placeholder="Search contacts…"
            data-testid="contacts-search"
            className="w-full pl-8 pr-3 py-2 bg-white border border-[#E5E5E5] rounded-lg text-sm text-[#181818] placeholder-[#C9C9C9] focus:outline-none focus:border-[#0176D3]/30"
          />
        </div>
      </div>

      {/* Table */}
      <div className="bg-[#F3F3F3] border border-[#E5E5E5] rounded-xl overflow-hidden" data-testid="contacts-list">
        {loading ? (
          <div className="flex items-center justify-center py-16">
            <Loader2 className="w-6 h-6 animate-spin text-[#0176D3]" />
          </div>
        ) : contacts.length === 0 ? (
          <EmptyState
            icon={Users}
            title="No Contacts yet"
            note={
              search
                ? `No contacts matching "${search}"`
                : "Contacts will appear here when Leads are converted or Contacts are created."
            }
          />
        ) : (
          <>
            {/* ── Mobile card list ─────────────────────────────────────────── */}
            <div className="sm:hidden divide-y divide-[#E5E5E5]">
              {contacts.map(c => (
                <div
                  key={c.id}
                  data-testid="contact-row"
                  onClick={() => navigate(`/crm/contacts/${c.id}`)}
                  className="flex items-center gap-3 px-4 py-3.5 cursor-pointer hover:bg-[#EEF4FF] transition-colors"
                >
                  <div className="w-8 h-8 rounded-full bg-gradient-to-br from-emerald-500/20 to-teal-500/20 border border-[#E5E5E5] flex items-center justify-center flex-shrink-0">
                    <span className="text-xs font-bold text-[#2E844A]">{(c.name || "?").charAt(0)}</span>
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium text-[#181818] truncate">{c.name || "—"}</p>
                    <p className="text-xs text-[#706E6B] mt-0.5 truncate">{c.email || c.phone || "—"}
                      {c.account_name ? ` · ${c.account_name}` : ""}
                    </p>
                  </div>
                  <div className="flex items-center gap-1 flex-shrink-0">
                    <button
                      data-testid="edit-contact-btn-mobile"
                      onClick={e => { e.stopPropagation(); setEditContact(c); }}
                      className="p-1.5 rounded text-[#706E6B] hover:text-[#0176D3] transition-colors"
                    >
                      <Pencil className="w-3.5 h-3.5" />
                    </button>
                    <ChevronRight className="w-4 h-4 text-[#706E6B]" />
                  </div>
                </div>
              ))}
            </div>
            {/* ── Desktop table ─────────────────────────────────────────────── */}
            <div className="hidden sm:block overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b border-[#E5E5E5] bg-white">
                    <Th>Name</Th>
                    <Th className="hidden md:table-cell">Account</Th>
                    <Th>Email</Th>
                    <Th className="hidden md:table-cell">Phone</Th>
                    <Th className="hidden lg:table-cell">Preferred Channel</Th>
                    <Th className="hidden lg:table-cell">Role</Th>
                    <th className="px-4 py-3 w-8" />
                  </tr>
                </thead>
                <tbody>
                  {contacts.map((c, idx) => {
                    const ChannelIcon = CHANNEL_ICONS[c.preferred_channel] || null;
                    return (
                      <tr
                        key={c.id}
                        data-testid="contact-row"
                        onClick={() => navigate(`/crm/contacts/${c.id}`)}
                        className={`border-b border-[#E5E5E5] cursor-pointer hover:bg-[#EEF4FF] transition-colors ${idx % 2 !== 0 ? "bg-white/30" : ""}`}
                      >
                        <td className="px-4 py-3">
                          <div className="flex items-center gap-2.5">
                            <div className="w-7 h-7 rounded-full bg-gradient-to-br from-emerald-500/20 to-teal-500/20 border border-[#E5E5E5] flex items-center justify-center flex-shrink-0">
                              <span className="text-[10px] font-bold text-[#2E844A] uppercase">
                                {(c.name || "?").charAt(0)}
                              </span>
                            </div>
                            <p className="text-[#181818] font-sans font-medium">{c.name || "—"}</p>
                          </div>
                        </td>
                        <td className="px-4 py-3 hidden md:table-cell">
                          {c.account_name ? (
                            <span className="flex items-center gap-1 text-xs text-[#706E6B]">
                              <Building2 className="w-3 h-3 text-[#706E6B]" /> {c.account_name}
                            </span>
                          ) : <span className="text-[#706E6B] text-xs">—</span>}
                        </td>
                        <td className="px-4 py-3">
                          <span className="text-xs text-[#706E6B] truncate block max-w-[180px]">{c.email || "—"}</span>
                        </td>
                        <td className="px-4 py-3 hidden md:table-cell">
                          <span className="text-xs text-[#706E6B]">{c.phone || "—"}</span>
                        </td>
                        <td className="px-4 py-3 hidden lg:table-cell">
                          {c.preferred_channel ? (
                            <span className="flex items-center gap-1 text-xs text-[#706E6B] font-mono">
                              {ChannelIcon && <ChannelIcon className="w-3 h-3" />}
                              {c.preferred_channel}
                            </span>
                          ) : <span className="text-[#706E6B] text-xs">—</span>}
                        </td>
                        <td className="px-4 py-3 hidden lg:table-cell">
                          <span className="text-xs text-[#706E6B]">{c.role || c.relationship || "—"}</span>
                        </td>
                        <td className="px-4 py-3 text-right">
                          <div className="flex items-center justify-end gap-1">
                            <button
                              data-testid="edit-contact-btn"
                              onClick={e => { e.stopPropagation(); setEditContact(c); }}
                              className="p-1.5 rounded text-[#706E6B] hover:text-[#0176D3] hover:bg-[#EEF4FF] transition-colors"
                              title="Edit contact"
                            >
                              <Pencil className="w-3.5 h-3.5" />
                            </button>
                            <ChevronRight className="w-4 h-4 text-[#706E6B]" />
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
            {/* ── end desktop table ───────────────────────────────────────── */}
            <Pagination page={page} total={total} size={PAGE_SIZE} onPage={setPage} />
          </>
        )}
      </div>
    </div>
  );
}

function Th({ children, className = "" }) {
  return (
    <th className={`text-left px-4 py-3 text-xs font-mono text-[#706E6B] uppercase tracking-wider ${className}`}>
      {children}
    </th>
  );
}

function EmptyState({ icon: Icon, title, note }) {
  return (
    <div className="py-16 text-center">
      <Icon className="w-8 h-8 text-[#444444] mx-auto mb-3" />
      <p className="text-sm text-[#706E6B] font-medium">{title}</p>
      {note && <p className="text-xs text-[#706E6B] mt-1 max-w-xs mx-auto">{note}</p>}
    </div>
  );
}

function Pagination({ page, total, size, onPage }) {
  if (total <= size) return null;
  return (
    <div className="flex items-center justify-between px-4 py-3 border-t border-[#E5E5E5]">
      <p className="text-xs text-[#706E6B]">
        Showing {page * size + 1}–{Math.min((page + 1) * size, total)} of {total}
      </p>
      <div className="flex gap-2">
        <button disabled={page === 0} onClick={() => onPage(p => p - 1)}
          className="px-3 py-1 text-xs border border-[#E5E5E5] rounded text-[#706E6B] hover:text-[#181818] disabled:opacity-40 disabled:cursor-not-allowed transition-colors">
          Prev
        </button>
        <button disabled={(page + 1) * size >= total} onClick={() => onPage(p => p + 1)}
          className="px-3 py-1 text-xs border border-[#E5E5E5] rounded text-[#706E6B] hover:text-[#181818] disabled:opacity-40 disabled:cursor-not-allowed transition-colors">
          Next
        </button>
      </div>
    </div>
  );
}
